import type { FastifyPluginAsync } from 'fastify'
import { AdminController } from './admin.controller.js'
import { adminMiddleware, requireRole } from './admin.middleware.js'
import { authMiddleware } from '../../middleware/auth.middleware.js'

export const adminRoutes: FastifyPluginAsync = async (fastify) => {
  const controller = new AdminController()

  const requireAdmin = [authMiddleware, adminMiddleware]
  const requireModerator = [authMiddleware, requireRole('moderator', 'admin', 'superadmin')]
  const requireSuperAdmin = [authMiddleware, requireRole('admin', 'superadmin')]

  fastify.get('/stats', { preHandler: requireAdmin }, controller.getStats.bind(controller))

  fastify.get('/users', { preHandler: requireAdmin }, controller.getUsers.bind(controller))
  fastify.get('/users/:userId', { preHandler: requireAdmin }, controller.getUser.bind(controller))

  fastify.patch('/users/:userId/status', { preHandler: requireAdmin }, controller.updateUserStatus.bind(controller))
  fastify.patch('/users/:userId/roles', { preHandler: requireSuperAdmin }, controller.updateUserRoles.bind(controller))
  fastify.patch('/users/:userId/plan', { preHandler: requireAdmin }, controller.changeUserPlan.bind(controller))
  fastify.post('/users/:userId/quota/reset', { preHandler: requireAdmin }, controller.resetUserQuota.bind(controller))
  fastify.post('/users/:userId/suspend', { preHandler: requireAdmin }, controller.suspendUser.bind(controller))
  fastify.post('/users/:userId/ban', { preHandler: requireAdmin }, controller.banUser.bind(controller))
  fastify.post('/users/:userId/token/reset', { preHandler: requireAdmin }, controller.resetUserTokens.bind(controller))
  fastify.post('/users/:userId/cache/purge', { preHandler: requireAdmin }, controller.purgeUserCache.bind(controller))
  fastify.post('/users/:userId/onboarding/resend', { preHandler: requireAdmin }, controller.resendOnboarding.bind(controller))
  fastify.get('/users/:userId/diagnostic', { preHandler: requireAdmin }, controller.getUserDiagnostic.bind(controller))
  
  // User activation management (for production maintenance mode)
  fastify.post('/users/:userId/activate', { preHandler: requireAdmin }, controller.activateUser.bind(controller))
  fastify.post('/users/:userId/deactivate', { preHandler: requireAdmin }, controller.deactivateUser.bind(controller))

  fastify.get('/moderation', { preHandler: requireModerator }, controller.getModerationQueue.bind(controller))
  fastify.get('/moderation/stats', { preHandler: requireModerator }, controller.getModerationStats.bind(controller))
  fastify.patch('/moderation/flags/:flagId', { preHandler: requireModerator }, controller.resolveFlag.bind(controller))

  fastify.get('/cache/stats', { preHandler: requireAdmin }, controller.getCacheStats.bind(controller))
  fastify.post('/cache/clear', { preHandler: requireAdmin }, controller.clearCache.bind(controller))
  fastify.post('/cache/sweep', { preHandler: requireAdmin }, controller.sweepCache.bind(controller))
  fastify.delete('/cache/entry', { preHandler: requireAdmin }, controller.deleteCacheEntry.bind(controller))

  fastify.get('/audit/logs', { preHandler: requireAdmin }, controller.getAuditLogs.bind(controller))
  fastify.get('/health', { preHandler: requireAdmin }, controller.getSystemHealth.bind(controller))
  fastify.get('/health/history', { preHandler: requireAdmin }, controller.getHealthHistory.bind(controller))
  fastify.get('/health/stats', { preHandler: requireAdmin }, controller.getHealthStats.bind(controller))
  fastify.get('/health/suggestions', { preHandler: requireAdmin }, controller.getHealthSuggestions.bind(controller))
  fastify.get('/health/trends', { preHandler: requireAdmin }, controller.getHealthTrends.bind(controller))
  fastify.get('/health/comparison', { preHandler: requireAdmin }, controller.getServiceComparison.bind(controller))
  fastify.get('/health/alerts', { preHandler: requireAdmin }, controller.getSecurityAlerts.bind(controller))

  // Incident management routes
  await fastify.register(async (fastify) => {
    const { adminIncidentRoutes } = await import('./admin.incident.controller.js')
    fastify.addHook('onRequest', authMiddleware)
    fastify.addHook('onRequest', adminMiddleware)
    await fastify.register(adminIncidentRoutes, { prefix: '/incidents' })
  })

  // Analytics routes
  await fastify.register(async (fastify) => {
    const { adminAnalyticsRoutes } = await import('./admin.analytics.controller.js')
    fastify.addHook('onRequest', authMiddleware)
    fastify.addHook('onRequest', adminMiddleware)
    await fastify.register(adminAnalyticsRoutes, { prefix: '/analytics' })
  })
}
